import { ArticleLayout } from '#app/features/writing/article-layout.tsx'
import { HarnessDiagram } from '#app/features/portfolio/components/harness-diagram.tsx'

export const article = {
	title: 'What are the types of agentic harness?',
	date: '2026-06-29',
	description:
		'Harnesses are told apart by what the model is pointed at: the code, the work, a shipped product, or the harness itself.',
	slug: 'types-of-agentic-harness',
	draft: false,
	hardProblem:
		'"Agentic harness" names multiple different systems that share a shape and little else. Which one is meant decides who operates it, what it may touch, and what happens when the model is wrong.',
	approach:
		'Separate them by what the model acts on — code, domain work, or a product surface — then judge each by what it stops when the model is wrong, which differs far more than what any of them can do.',
	tags: ['Engineering', 'AI'],
}

export default (props) => <ArticleLayout article={article} {...props} />

What separates one harness from another is what the model is pointed at.

## 1. Acting on code

The model writes code and the harness is what that code has to survive: lint,
formatter, typecheck, tests, and hooks that refuse destructive commands.

## 2. Acting on the work

> Our moat here was our understanding of our internal processes, the tacit
> knowledge that you need to run successful AI — and this is true no matter how
> good AI gets, how good models get.
>
> — [Your Moat Is Your Data Model, Mike Phipps, Gates Foundation](https://www.youtube.com/watch?v=jt1Pbr_n6oU)

Domain work arrives as evidence no schema anticipated, and reading that mess is
what models are good at, so the model reads and proposes while a deterministic
engine decides and records.

<HarnessDiagram />

## 3. Acting on a shipped surface

> It's better to fail predictably than it is to succeed unpredictably.
>
> — [Anthropic Workshop: Build Agents That Run for Hours](https://www.youtube.com/watch?v=mR-WAvEPRwE)

The same engine behind something other people run. The agent gets a public surface
— the tools it exposes, the container it runs in — and the goal stops being
throughput and becomes trust: that it does what it says and no more.

## 4. Acting on the harness

> A model request is not permission. Authority should bind to one pending action.
>
> — [Your Agent Didn't Fail. Your Harness Did., Vinoth Govindarajan, OpenAI](https://www.youtube.com/watch?v=BInpv7lGp1o)

A model pointed at the session itself: noticing that the operator is doing by
hand what the harness already automates, explaining when asked, blocking the
calls that would destroy someone else's work. Notice, explain, block — the
input, the question, the action. It produces nothing, and that is the point.

The interesting question about any harness is not what it can do, since models are
capable now, but what it is stopped from doing when it is wrong.
